/**
 * Fleet Management React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FleetService } from '../api/services/fleetService';
import type {
  Organization,
  OrganizationMember,
  Site,
  Device,
  DeviceHeartbeat,
  DeviceEvent,
  AlertConfig,
  AlertHistory,
  FleetOverview,
  FleetMapData,
} from '../api/types';

// Query keys
export const fleetKeys = {
  all: ['fleet'] as const,
  overview: () => [...fleetKeys.all, 'overview'] as const,
  map: () => [...fleetKeys.all, 'map'] as const,
  alerts: (hours?: number) => [...fleetKeys.all, 'alerts', hours] as const,

  organizations: {
    all: ['organizations'] as const,
    list: (params?: any) => [...fleetKeys.organizations.all, 'list', params] as const,
    detail: (id: string) => [...fleetKeys.organizations.all, id] as const,
    members: (id: string) => [...fleetKeys.organizations.all, id, 'members'] as const,
  },

  sites: {
    all: ['sites'] as const,
    list: (params?: any) => [...fleetKeys.sites.all, 'list', params] as const,
    detail: (id: string) => [...fleetKeys.sites.all, id] as const,
    devices: (id: string) => [...fleetKeys.sites.all, id, 'devices'] as const,
  },

  devices: {
    all: ['devices'] as const,
    list: (params?: any) => [...fleetKeys.devices.all, 'list', params] as const,
    detail: (id: string) => [...fleetKeys.devices.all, id] as const,
    heartbeats: (id: string, days?: number) => [...fleetKeys.devices.all, id, 'heartbeats', days] as const,
    events: (id: string) => [...fleetKeys.devices.all, id, 'events'] as const,
  },

  alertConfigs: {
    all: ['alertConfigs'] as const,
    list: (orgId?: string) => [...fleetKeys.alertConfigs.all, 'list', orgId] as const,
    detail: (id: string) => [...fleetKeys.alertConfigs.all, id] as const,
    history: (configId?: string) => [...fleetKeys.alertConfigs.all, 'history', configId] as const,
  },
};

// Fleet Overview
export function useFleetOverview() {
  return useQuery({
    queryKey: fleetKeys.overview(),
    queryFn: (): Promise<FleetOverview> => FleetService.getOverview(),
    staleTime: 30000, // 30 seconds
  });
}

// Fleet Map Data
export function useFleetMap() {
  return useQuery({
    queryKey: fleetKeys.map(),
    queryFn: (): Promise<FleetMapData> => FleetService.getMapData(),
    staleTime: 60000, // 1 minute
  });
}

// Fleet Alerts
export function useFleetAlerts(hours: number = 24) {
  return useQuery({
    queryKey: fleetKeys.alerts(hours),
    queryFn: (): Promise<DeviceEvent[]> => FleetService.getAlerts(hours),
    staleTime: 30000,
  });
}

// Organizations
export function useOrganizations(params?: { page?: number; size?: number }) {
  return useQuery({
    queryKey: fleetKeys.organizations.list(params),
    queryFn: () => FleetService.listOrganizations(params),
  });
}

export function useOrganization(id: string) {
  return useQuery({
    queryKey: fleetKeys.organizations.detail(id),
    queryFn: (): Promise<Organization> => FleetService.getOrganization(id),
    enabled: !!id,
  });
}

export function useOrganizationMembers(id: string) {
  return useQuery({
    queryKey: fleetKeys.organizations.members(id),
    queryFn: (): Promise<OrganizationMember[]> => FleetService.getOrganizationMembers(id),
    enabled: !!id,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Organization>) => FleetService.createOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fleetKeys.organizations.all });
      queryClient.invalidateQueries({ queryKey: fleetKeys.overview() });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Organization> }) =>
      FleetService.updateOrganization(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: fleetKeys.organizations.all });
      queryClient.invalidateQueries({ queryKey: fleetKeys.organizations.detail(id) });
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => FleetService.deleteOrganization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fleetKeys.organizations.all });
      queryClient.invalidateQueries({ queryKey: fleetKeys.overview() });
    },
  });
}

// Sites
export function useSites(params?: { page?: number; size?: number; organization_id?: string }) {
  return useQuery({
    queryKey: fleetKeys.sites.list(params),
    queryFn: () => FleetService.listSites(params),
  });
}

export function useSite(id: string) {
  return useQuery({
    queryKey: fleetKeys.sites.detail(id),
    queryFn: (): Promise<Site> => FleetService.getSite(id),
    enabled: !!id,
  });
}

export function useSiteDevices(id: string) {
  return useQuery({
    queryKey: fleetKeys.sites.devices(id),
    queryFn: (): Promise<Device[]> => FleetService.getSiteDevices(id),
    enabled: !!id,
  });
}

export function useCreateSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Site>) => FleetService.createSite(data),
    onSuccess: () => {
      // Invalidate and refetch all site-related queries
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.refetchQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: fleetKeys.overview() });
      queryClient.invalidateQueries({ queryKey: fleetKeys.map() });
      queryClient.invalidateQueries({ queryKey: fleetKeys.organizations.all });
    },
  });
}

export function useUpdateSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Site> }) =>
      FleetService.updateSite(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.refetchQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: fleetKeys.sites.detail(id) });
      queryClient.invalidateQueries({ queryKey: fleetKeys.map() });
    },
  });
}

export function useDeleteSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => FleetService.deleteSite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.refetchQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: fleetKeys.overview() });
      queryClient.invalidateQueries({ queryKey: fleetKeys.map() });
      queryClient.invalidateQueries({ queryKey: fleetKeys.organizations.all });
    },
  });
}

// Devices
export function useDevices(params?: {
  page?: number;
  size?: number;
  organization_id?: string;
  site_id?: string;
  status?: string;
}) {
  return useQuery({
    queryKey: fleetKeys.devices.list(params),
    queryFn: () => FleetService.listDevices(params),
  });
}

export function useDevice(id: string) {
  return useQuery({
    queryKey: fleetKeys.devices.detail(id),
    queryFn: (): Promise<Device> => FleetService.getDevice(id),
    enabled: !!id,
  });
}

export function useDeviceHeartbeats(id: string, days: number = 7) {
  return useQuery({
    queryKey: fleetKeys.devices.heartbeats(id, days),
    queryFn: (): Promise<DeviceHeartbeat[]> => FleetService.getDeviceHeartbeats(id, days),
    enabled: !!id,
  });
}

export function useDeviceEvents(id: string) {
  return useQuery({
    queryKey: fleetKeys.devices.events(id),
    queryFn: (): Promise<DeviceEvent[]> => FleetService.getDeviceEvents(id),
    enabled: !!id,
  });
}

export function useCreateDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Device>) => FleetService.createDevice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fleetKeys.devices.all });
      queryClient.invalidateQueries({ queryKey: fleetKeys.overview() });
      queryClient.invalidateQueries({ queryKey: fleetKeys.map() });
    },
  });
}

export function useUpdateDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Device> }) =>
      FleetService.updateDevice(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: fleetKeys.devices.all });
      queryClient.invalidateQueries({ queryKey: fleetKeys.devices.detail(id) });
    },
  });
}

export function useDeleteDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => FleetService.deleteDevice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fleetKeys.devices.all });
      queryClient.invalidateQueries({ queryKey: fleetKeys.overview() });
      queryClient.invalidateQueries({ queryKey: fleetKeys.map() });
    },
  });
}

export function useRegenerateDeviceToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => FleetService.regenerateDeviceToken(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: fleetKeys.devices.detail(id) });
    },
  });
}

// Alert Configs
export function useAlertConfigs(organizationId?: string) {
  return useQuery({
    queryKey: fleetKeys.alertConfigs.list(organizationId),
    queryFn: (): Promise<AlertConfig[]> => FleetService.listAlertConfigs(organizationId),
  });
}

export function useAlertConfig(id: string) {
  return useQuery({
    queryKey: fleetKeys.alertConfigs.detail(id),
    queryFn: (): Promise<AlertConfig> => FleetService.getAlertConfig(id),
    enabled: !!id,
  });
}

export function useAlertHistory(configId?: string) {
  return useQuery({
    queryKey: fleetKeys.alertConfigs.history(configId),
    queryFn: (): Promise<AlertHistory[]> => FleetService.getAlertHistory(configId),
  });
}

export function useCreateAlertConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<AlertConfig>) => FleetService.createAlertConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fleetKeys.alertConfigs.all });
    },
  });
}

export function useUpdateAlertConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AlertConfig> }) =>
      FleetService.updateAlertConfig(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: fleetKeys.alertConfigs.all });
      queryClient.invalidateQueries({ queryKey: fleetKeys.alertConfigs.detail(id) });
    },
  });
}

export function useDeleteAlertConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => FleetService.deleteAlertConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fleetKeys.alertConfigs.all });
    },
  });
}

export function useTestAlertWebhook() {
  return useMutation({
    mutationFn: (id: string) => FleetService.testAlertWebhook(id),
  });
}
